import { date } from 'quasar'
import i18n from 'src/i18n'
import { msPerDay, msPerHour, msPerMinute, msPerMonth, msPerSecond, msPerYear } from 'src/models/model/constants'
import IsoDate from 'src/models/model/primitive-types/IsoDate'
import { timeLabel } from 'src/models/model/utils/time-utils'
import { reactive } from 'vue'

export default class IsoDateTime extends Date {
  static name = 'IsoDateTime'

  static get currentTime () {
    return timeContainer.currentTime
  }

  static get currentTimeWithSeconds () {
    return timeContainer.currentTimeWithSeconds
  }

  get currentTime () {
    return timeContainer.currentTime
  }

  get currentTimeWithSeconds () {
    return timeContainer.currentTimeWithSeconds
  }

  static parse (value) {
    if (!value) {
      return null
    } else if (_.isString(value)) {
      return new IsoDateTime(value)
    } else {
      return new IsoDateTime(value)
    }
  }

  static encode (value) {
    if (!value) return value

    return value.toISOString()
  }

  isDate () {
    return this.toDateString() !== 'Invalid Date'
  }

  format (format) {
    return date.formatDate(this, format)
  }

  toString () {
    return this.format('YYYY MMM D, h:mm a')
  }

  i18n (format) {
    if (!this.isDate()) return null

    return i18n.global.d(new Date(this), format)
  }

  inTimeZone (timeZone) {
    return new IsoDateTime(this.toLocaleString('en-US', {
      timeZone
    }))
  }

  relativeTime ({ relativeTo = null, duration = false, compact = false, label = true, allowNegativeDuration = true, msPrecisionLimit = false, secondsDecimals = 1 } = {}) {
    if (!relativeTo) {
      relativeTo = this.currentTimeWithSeconds
    }

    const elapsed = Math.abs(relativeTo - this)

    let durationDescription = ''
    if (elapsed < 4 * msPerSecond && secondsDecimals > 0) {
      // Show a decimal point for less than 4s ... e.g 3.7s
      const roundingFactor = Math.pow(10, secondsDecimals)
      const value = Math.round(elapsed / 1000 * roundingFactor) / roundingFactor
      durationDescription = `${value}`
      if (label) durationDescription += ` ${timeLabel('second', compact)}${((value === 1 || compact) ? '' : 's')}`
    } else if (elapsed < msPerMinute) {
      const value = Math.round(elapsed / 1000)
      durationDescription = `${value}`
      if (label) durationDescription += ` ${timeLabel('second', compact)}${((value === 1 || compact) ? '' : 's')}`
    } else if (!!msPrecisionLimit && elapsed < msPrecisionLimit) {
      const minutesValue = Math.floor(elapsed / msPerMinute)
      const elapsedMinutesMs = minutesValue * msPerMinute
      const secondsValue = Math.floor((elapsed - elapsedMinutesMs) / msPerSecond)
      durationDescription = `${minutesValue}:${secondsValue.toString().padStart(2, '0')}`
      if (label) durationDescription += ` ${timeLabel('minute', compact)}${((minutesValue === 1 || compact) ? '' : 's')}`
    } else if (elapsed < msPerHour) {
      const value = Math.round(elapsed / msPerMinute)
      durationDescription = `${value}`
      if (label) durationDescription += ` ${timeLabel('minute', compact)}${((value === 1 || compact) ? '' : 's')}`
    } else if (elapsed < msPerDay) {
      const value = Math.round(elapsed / msPerHour)
      durationDescription = `${value}`
      if (label) durationDescription += ` ${timeLabel('hour', compact)}${((value === 1 || compact) ? '' : 's')}`
    } else if (elapsed < msPerMonth) {
      const value = Math.round(elapsed / msPerDay)
      durationDescription = `${value}`
      if (label) durationDescription += ` ${timeLabel('day', compact)}${((value === 1 || compact) ? '' : 's')}`
    } else if (elapsed < msPerYear) {
      const value = Math.round(elapsed / msPerMonth)
      durationDescription = `${value}`
      if (label) durationDescription += ` ${timeLabel('month', compact)}${((value === 1 || compact) ? '' : 's')}`
    } else {
      const value = Math.round(elapsed / msPerYear)
      durationDescription = `${value}`
      if (label) durationDescription += ` ${timeLabel('year', compact)}${((value === 1 || compact) ? '' : 's')}`
    }

    if (duration) {
      if (!allowNegativeDuration && (relativeTo - this) > 0) {
        durationDescription = '0'
        if (label) durationDescription += ` ${timeLabel('second', compact)}`
      }

      return durationDescription
    }

    const past = this < relativeTo
    return ((past || compact) ? '' : 'in ') + durationDescription + (past ? ' ago' : '')
  }

  smartRelativeTime (options = {}) {
    const currentTime = options.relativeTo || this.currentTime
    const isToday = i18n.global.d(currentTime, 'shortDate') === i18n.global.d(this, 'shortDate')

    if (!isToday) {
      return `${i18n.global.d(this, 'shortDate')} ${i18n.global.d(this, 'shortTime')}`
    } else {
      const moreThanAnHourAgo = ((currentTime - this) / (1000 * 60)) > 60
      return moreThanAnHourAgo
        ? i18n.global.d(this, 'shortTime')
        : this.relativeTime({ relativeTo: currentTime, ...options })
    }
  }

  toIsoDate () {
    return IsoDate.parse(this.format('YYYY-MM-DD'))
  }
}

function getNewTime (withSeconds = false) {
  const newTime = new IsoDateTime()
  if (!withSeconds) {
    // Set the current time to the current time, but with the seconds and milliseconds set to 0 and 1 respectively
    // This is to avoid updating the time every second which can cause a lot of re-renders
    newTime.setSeconds(0)
    newTime.setMilliseconds(1)
  }
  return newTime
}

export const timeContainer = reactive({ currentTime: getNewTime(), currentTimeWithSeconds: getNewTime(true) })
// Check for new time every 1 second, only update if the minute has changed
setInterval(() => {
  const newTime = getNewTime()
  if (newTime.toISOString() !== timeContainer.currentTime.toISOString()) {
    timeContainer.currentTime = newTime
  }

  timeContainer.currentTimeWithSeconds = getNewTime(true)
}, 1000)
